import React, { useState, useEffect } from 'react';
import axios from '../../../../../../api/axios';

function HRConfiguration() {
  const businessEntityId = sessionStorage.getItem("configBizEntityId");

  const [paidDaysOff, setPaidDaysOff] = useState('');
  const [documents, setDocuments] = useState([{ id: 1, name: '', required: false }]);
  const [editPermissions, setEditPermissions] = useState({
    attendanceEdit: false,
    wageEdit: false,
    paidDaysOffEdit: false
  });

  // Fetch existing configuration on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/hr/${businessEntityId}`);
        if (response.data) {
          // Convert numeric values to actual paid days off
          setPaidDaysOff(response.data.paid_days_off?.toString() || '');
          
          // Handle required documents
          if (response.data.required_documents && Array.isArray(response.data.required_documents)) {
            setDocuments(response.data.required_documents.map(doc => ({
              id: doc.id || Math.random(), // Use existing id or generate random one
              name: doc.name || '',
              required: Boolean(doc.required)
            })));
          }

          // Convert numeric 0/1 values to boolean
          setEditPermissions({
            attendanceEdit: Boolean(response.data.attendance_edit),
            wageEdit: Boolean(response.data.wage_edit),
            paidDaysOffEdit: Boolean(response.data.paid_days_off_edit)
          });
        }
      } catch (error) {
        console.error('Error fetching configuration:', error);
      }
    };

    if (businessEntityId) {
      fetchData();
    }
  }, [businessEntityId]);

  // Add new document field
  const addDocument = () => {
    const newId = Math.max(...documents.map(doc => doc.id), 0) + 1;
    setDocuments([...documents, { id: newId, name: '', required: false }]);
  };

  // Remove document field
  const removeDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  // Handle document name change
  const handleDocNameChange = (id, value) => {
    setDocuments(documents.map(doc => (doc.id === id ? { ...doc, name: value } : doc)));
  };

  // Handle document required status change
  const handleRequiredChange = (id) => {
    setDocuments(documents.map(doc => (doc.id === id ? { ...doc, required: !doc.required } : doc)));
  };

  const handleToggleChange = (permission) => {
    setEditPermissions({
      ...editPermissions,
      [permission]: !editPermissions[permission]
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

 

    const configData = {
      business_entity_id: businessEntityId,
      attendance_edit: editPermissions.attendanceEdit ? 1 : 0,
      salary_edit: editPermissions.wageEdit ? 1 : 0,
      paid_days_off_edit: editPermissions.paidDaysOffEdit ? 1 : 0,
      paid_days_off: parseInt(paidDaysOff, 10) || 0,
      required_documents: JSON.stringify(documents)
      
    };

    try {
      const response = await axios.post('/hr/upsert-hr-configuration', configData);
      alert('Configuration saved successfully!');
      console.log('Response:', response.data);
    } catch (error) {
      alert('Error saving configuration: ' + (error.response?.data?.message || error.message));
      console.error('Error:', error);
    }
  };

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header">
          <h4 className="mb-0">Employee Configuration Settings</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <h5 className="mb-3">Edit Permissions</h5>
            <div className="row">
              {/* Attendance Edit Permission */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Attendance Edit</h6>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="attendanceEditSwitch"
                          checked={editPermissions.attendanceEdit}
                          onChange={() => handleToggleChange('attendanceEdit')}
                        />
                        <label className="form-check-label text-nowrap" htmlFor="attendanceEditSwitch">
                          {editPermissions.attendanceEdit ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wage Edit Permission */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Wage Edit</h6>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="wageEditSwitch"
                          checked={editPermissions.wageEdit}
                          onChange={() => handleToggleChange('wageEdit')}
                        />
                        <label className="form-check-label text-nowrap" htmlFor="wageEditSwitch">
                          {editPermissions.wageEdit ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paid Days Off Edit Permission */}
              <div className="col-md-4 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Paid Days Off Edit</h6>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="paidDaysOffEditSwitch"
                          checked={editPermissions.paidDaysOffEdit}
                          onChange={() => handleToggleChange('paidDaysOffEdit')}
                        />
                        <label className="form-check-label text-nowrap" htmlFor="paidDaysOffEditSwitch">
                          {editPermissions.paidDaysOffEdit ? 'Enabled' : 'Disabled'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Paid Days Off Section - Uncomment if needed */}
            {/* <div className="mb-4">
              <label className="form-label">
                <h5>Monthly Paid Days Off</h5>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={paidDaysOff}
                  onChange={(e) => setPaidDaysOff(e.target.value)}
                  placeholder="Enter number of paid days off"
                  min="0"
                />
                <span className="input-group-text">days</span>
              </div>
            </div> */}

            {/* Document Requirements Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Required Documents</h5>
                <button type="button" className="btn btn-primary btn-sm" onClick={addDocument}>
                  Add Document
                </button>
              </div>

              {documents.map((doc) => (
                <div key={doc.id} className="row mb-3 align-items-center">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      value={doc.name}
                      onChange={(e) => handleDocNameChange(doc.id, e.target.value)}
                      placeholder="Document name"
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    {/* <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`required-${doc.id}`}
                        checked={doc.required}
                        onChange={() => handleRequiredChange(doc.id)}
                      />
                      <label className="form-check-label" htmlFor={`required-${doc.id}`}>
                        Required
                      </label>
                    </div> */}
                  </div>
                  <div className="col-md-2">
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => removeDocument(doc.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="text-end">
              <button type="submit" className="btn btn-success">
                Save Configuration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default HRConfiguration;